<template>
  <div v-if="inlineTableEnabled">
    <wrap
      v-if="inlineTable.module"
      :block="{style: {wrap: 'Card'}}"
      :scrollable-body="false"
    >
      <template #default>
        <div
          class="d-flex position-relative h-100"
          :class="{ 'overflow-hidden': !inlineTable.items.length || processing }"
        >
          <b-table-simple
            hover
            responsive
            sticky-header
            class="border-top mh-100 h-100 mb-0"
          >
            <b-thead>
              <b-tr>
                <b-th
                  v-for="field in inlineTable.fields"
                  :key="field.key"
                  sticky-column
                  class="pr-0"
                  :style="{
                    cursor: field.sortable ? 'pointer' : 'default',
                  }"
                  @click.stop.prevent="handleSort(field)"
                >
                  <div
                    class="d-flex align-self-center"
                  >
                    <div
                      :class="{ required: field.required }"
                      class="d-flex align-self-center text-nowrap"
                    >
                      {{ field.label }}
                    </div>
                    <div
                      class="d-flex"
                    >
                      <hint
                        v-if="field.hint"
                        :id="field.key"
                        :text="field.hint"
                        class="d-inline-block"
                      />
                      <b-button
                        v-if="field.sortable"
                        variant="link p-0 ml-1"
                        :title="$t('block:recordList.sort.tooltip')"
                        class="d-flex align-items-center justify-content-center"
                      >
                        <font-awesome-layers
                          class="d-print-none"
                        >
                          <font-awesome-icon
                            :icon="['fas', 'angle-up']"
                            class="mb-1"
                            :style="{
                              color: 'gray',
                              ...sorterStyle(field, 'ASC'),
                            }"
                          />
                          <font-awesome-icon
                            :icon="['fas', 'angle-down']"
                            class="mt-1"
                            :style="{
                              color: 'gray',
                              ...sorterStyle(field, 'DESC'),
                            }"
                          />
                        </font-awesome-layers>
                      </b-button>
                    </div>
                  </div>
                </b-th>
              </b-tr>
            </b-thead>

            <b-tbody v-if="inlineTable.items.length && !inlineTable.processing">
              <b-tr
                v-for="(item, index) in inlineTable.items"
                :key="`${index}${item.r.recordID}`"
                :variant="!!item.r.deletedAt ? 'danger' : undefined"
                :class="'pointer'"
              >
                <b-td
                  v-for="field in inlineTable.fields"
                  :key="field.key"
                >
                  <div
                    v-if="field.moduleField.canReadRecordValue"
                    class="mb-0"
                  >
                    <field-viewer
                      :field="field.moduleField"
                      value-only
                      :record="item.r"
                      :module="inlineTable.module"
                      :namespace="namespace"
                    />
                  </div>
                  <i
                    v-else
                    class="text-primary"
                  >
                    {{ $t('field.noPermission') }}
                  </i>
                </b-td>
              </b-tr>
            </b-tbody>

            <div
              v-else
              class="position-absolute text-center mt-5 d-print-none"
              style="left: 0; right: 0;"
            >
              <b-spinner
                v-if="inlineTable.processing"
              />

              <h6
                v-else-if="!inlineTable.items.length"
                class="mb-0 mx-2"
              >
                {{ $t('block:recordList.noRecords') }}
              </h6>
            </div>
          </b-table-simple>
        </div>
      </template>

      <template
        v-if="showFooter"
        #footer
      >
        <b-container
          ref="footer"
          fluid
          class="m-0 p-2"
        >
          <b-row no-gutters>
            <b-col class="d-flex justify-content-between align-items-center">
              <div class="text-truncate">
                <div
                  v-if="inlineTableOptions.showTotalCount"
                  class="ml-2 text-nowrap font-weight-bold"
                >
                  <span v-if="inlineTable.pagination.count > inlineTableOptions.perPage">
                    {{ $t('block:recordList.pagination.showing', getPagination) }}
                  </span>
                  <span
                    v-else
                  >
                    {{ $t('block:recordList.pagination.single_plural', getPagination) }}
                  </span>
                </div>
              </div>
              <div
                v-if="showPageNavigation && !inlineTableOptions.hidePaging"
              >
                <b-pagination
                  v-if="inlineTableOptions.fullPageNavigation"
                  align="right"
                  aria-controls="record-list"
                  class="m-0 d-print-none"
                  pills
                  variant="link"
                  :value="getPagination.page"
                  :per-page="getPagination.perPage"
                  :total-rows="getPagination.count"
                  @change="goToPage"
                >
                  <template #first-text>
                    <font-awesome-icon :icon="['fas', 'angle-double-left']" />
                  </template>
                  <template #prev-text>
                    <font-awesome-icon :icon="['fas', 'angle-left']" />
                  </template>
                  <template #next-text>
                    <font-awesome-icon :icon="['fas', 'angle-right']" />
                  </template>
                  <template #last-text>
                    <font-awesome-icon :icon="['fas', 'angle-double-right']" />
                  </template>
                  <template #elipsis-text>
                    <font-awesome-icon :icon="['fas', 'ellipsis-h']" />
                  </template>
                </b-pagination>

                <b-button-group v-else>
                  <b-button
                    :disabled="!hasPrevPage"
                    variant="link"
                    class="text-dark"
                    @click.stop.prevent="goToPage()"
                  >
                    <font-awesome-icon :icon="['fas', 'angle-double-left']" />
                  </b-button>
                  <b-button
                    :disabled="!hasPrevPage"
                    variant="link"
                    class="text-dark"
                    @click.stop.prevent="goToPage('prevPage')"
                  >
                    <font-awesome-icon :icon="['fas', 'angle-left']" />
                    {{ $t('block:recordList.pagination.prev') }}
                  </b-button>
                  <b-button
                    :disabled="!hasNextPage"
                    variant="link"
                    class="text-dark"
                    @click.stop.prevent="goToPage('nextPage')"
                  >
                    {{ $t('block:recordList.pagination.next') }}
                    <font-awesome-icon :icon="['fas', 'angle-right']" />
                  </b-button>
                </b-button-group>
              </div>
            </b-col>
          </b-row>
        </b-container>
      </template>
    </wrap>
  </div>

  <div v-else>
    <b-spinner
      v-if="processing"
      variant="primary"
      small
    />
    <template v-else>
      <span
        v-for="(v, index) of formattedValue"
        :key="index"
        class="text-nowrap"
        :class="{ 'd-block': field.options.multiDelimiter === '\n' }"
      >
        <router-link
          :to="v.to"
          :class="{ 'text-decoration-none default-cursor': !v.to}"
        >
          {{ v.value }}{{ index !== formattedValue.length - 1 ? field.options.multiDelimiter : '' }}
        </router-link>
      </span>
    </template>
  </div>
</template>

<script>
import base from './base'
import { compose, NoID } from '@cortezaproject/corteza-js'
import { mapActions, mapGetters } from 'vuex'
import Wrap from 'corteza-webapp-compose/src/components/PageBlocks/Wrap'
import users from 'corteza-webapp-compose/src/mixins/users'

export default {
  name: 'Record',

  components: {
    Wrap,
    Hint: () => import('../../Common/Hint'),
    FieldViewer: () => import('./index'),
  },
  extends: base,

  mixins: [
    users,
  ],

  data () {
    return {
      processing: false,

      recordValues: {},

      inlineTable: {
        processing: false,
        module: undefined,

        fields: [],

        filter: {
          query: '',
          sort: '',
          limit: 10,
          pageCursor: '',
          prevPage: '',
          nextPage: '',
        },

        pagination: {
          pages: [],
          page: 1,
          count: 0,
        },

        items: [],

        sortBy: undefined,
        sortDirecton: undefined,
      },
    }
  },

  computed: {
    ...mapGetters({
      pages: 'page/set',
      findUserByID: 'user/findByID',
    }),

    formattedValue () {
      const value = Array.isArray(this.value) ? this.value : [this.value].filter(v => v) || []
      return value.map(recordID => {
        return {
          to: this.linkToRecord(recordID),
          value: this.recordValues[recordID] || recordID,
        }
      })
    },

    recordPage () {
      return this.pages.find(p => p.moduleID === this.field.options.moduleID)
    },

    inlineTableEnabled () {
      return this.inlineTableOptions.enabled && this.inlineTableOptions.fields.length > 0
    },

    getPagination () {
      const { page = 1, count = 0 } = this.inlineTable.pagination
      const { perPage = 10 } = this.inlineTableOptions

      return {
        from: ((page - 1) * perPage) + 1,
        to: Math.min((page * perPage), count),
        page,
        perPage,
        count,
      }
    },

    hasPrevPage () {
      return this.inlineTable.filter.prevPage
    },

    hasNextPage () {
      return this.inlineTable.filter.nextPage
    },

    showPageNavigation () {
      return this.hasNextPage || this.hasPrevPage
    },

    showFooter () {
      return !this.inlineTableOptions.hidePaging
    },

    inlineTableOptions () {
      return this.field.options.inlineTable || {}
    },
  },

  watch: {
    value: {
      immediate: true,
      handler (value) {
        if (this.inlineTableEnabled) {
          this.prepRecordList()
          this.refresh(true)
        } else {
          this.formatRecordValues(value)
        }
      },
    },

    field: {
      deep: true,
      handler () {
        if (this.inlineTableOptions.enabled) {
          this.prepRecordList()
          this.refresh(true)
        }
      },
    },
  },

  methods: {
    ...mapActions({
      findModuleByID: 'module/findByID',
      resolveUsers: 'user/fetchUsers',
    }),

    linkToRecord (recordID) {
      if (!this.recordPage || !recordID) {
        return ''
      }

      return {
        name: 'page.record',
        params: {
          pageID: this.recordPage.pageID,
          recordID: recordID,
        },
      }
    },

    prepRecordList () {
      this.inlineTable.filter = {
        limit: this.inlineTableOptions.perPage,
      }
    },

    async formatRecordValues (value) {
      value = Array.isArray(value) ? value : [value].filter(v => v) || []
      const { namespaceID = NoID } = this.namespace
      const { moduleID = NoID, labelField, recordLabelField } = this.field.options

      if (!value.length || [moduleID, namespaceID].includes(NoID) || !labelField) {
        return
      }

      this.processing = true

      // Get configured module/field
      return this.findModuleByID({ namespace: this.namespace, moduleID }).then(module => {
        let relatedField = module.fields.find(({ name }) => name === labelField)
        const query = value.map(recordID => `recordID = ${recordID}`).join(' OR ')

        return this.$ComposeAPI.recordList({ namespaceID, moduleID, query, deleted: 1 }).then(async ({ set = [] }) => {
          if (recordLabelField) {
            set = await this.findModuleByID({ namespaceID, moduleID: relatedField.options.moduleID }).then(relatedModule => {
              const mappedIDs = {}
              const queryIDs = []

              set.forEach(r => {
                r = new compose.Record(module, r)
                mappedIDs[r.values[labelField]] = r.recordID
                queryIDs.push(`recordID = ${r.values[labelField]}`)
              })

              return this.$ComposeAPI.recordList({ namespaceID, moduleID: relatedField.options.moduleID, query: queryIDs.join(' OR '), deleted: 1 }).then(({ set = [] }) => {
                relatedField = relatedModule.fields.find(({ name }) => name === this.field.options.recordLabelField)
                return set.map(r => {
                  r.recordID = mappedIDs[r.recordID]
                  return new compose.Record(relatedModule, r)
                })
              })
            })
          } else {
            set = set.map(r => new compose.Record(module, r))
          }

          for (const record of set) {
            let recordValue = relatedField.isMulti ? record.values[relatedField.name] : [record.values[relatedField.name]]

            if (recordValue.length && relatedField.kind === 'User') {
              recordValue = await Promise.all(recordValue.map(async v => {
                if (!this.findUserByID(v)) {
                  await this.resolveUsers(v)
                }

                return relatedField.formatter(this.findUserByID(v))
              }))
            }

            this.$set(this.recordValues, record.recordID, recordValue.join(relatedField.options.multiDelimiter))
          }
        })
      }).finally(() => {
        setTimeout(() => {
          this.processing = false
        }, 300)
      })
    },

    refresh (resetPagination = false) {
      this.pullRecords(resetPagination)
    },

    async pullRecords (resetPagination = true) {
      const value = Array.isArray(this.value) ? this.value : [this.value].filter(v => v) || []
      const { namespaceID = NoID } = this.namespace
      const { moduleID = NoID } = this.field.options

      if (!value.length || [moduleID, namespaceID].includes(NoID)) {
        return
      }

      this.processing = true

      // Get configured module/field
      return this.findModuleByID({ namespace: this.namespace, moduleID }).then(module => {
        this.inlineTable.module = module

        const fields = module.filterFields(this.inlineTableOptions.fields)
        this.inlineTable.fields = fields.map(mf => ({
          key: mf.name,
          hint: (mf.options.hint || {}).view,
          label: mf.isSystem ? this.$t(`field:system.${mf.name}`) : mf.label || mf.name,
          moduleField: mf,
          sortable: true,
          tdClass: 'record-value',
          required: mf.isRequired,
        }))

        const query = value.map(recordID => `recordID = ${recordID}`).join(' OR ')

        if (this.inlineTable.filter.pageCursor) {
          this.inlineTable.filter.sort = ''
        }

        let paginationOptions = {}
        if (resetPagination) {
          const { fullPageNavigation = false, showTotalCount = false } = this.inlineTableOptions
          paginationOptions = {
            incPageNavigation: fullPageNavigation,
            incTotal: showTotalCount,
          }
        }

        return this.$ComposeAPI.recordList({ ...this.inlineTable.filter, moduleID, namespaceID, query, ...paginationOptions })
          .then(({ set, filter }) => {
            const records = set.map(r => new compose.Record(r, this.inlineTable.module))

            this.inlineTable.filter = { ...this.inlineTable.filter, ...filter }
            this.inlineTable.filter.pageCursor = undefined
            this.inlineTable.filter.nextPage = filter.nextPage
            this.inlineTable.filter.prevPage = filter.prevPage

            if (resetPagination) {
              let count = this.inlineTable.pagination.count || 0

              if (paginationOptions.incTotal) {
                count = filter.total || 0
                this.inlineTable.filter.incTotal = false
              }

              if (paginationOptions.incPageNavigation) {
                const pages = filter.pageNavigation || []
                this.inlineTable.pagination.pages = pages

                if (!paginationOptions.incTotal) {
                  if (pages.length > 1) {
                    const lastPageCount = pages[pages.length - 1].items
                    count = ((pages.length - 1) * this.inlineTableOptions.perPage) + lastPageCount
                  } else {
                    count = records.length
                  }
                }

                this.inlineTable.filter.incPageNavigation = false
              }

              this.inlineTable.pagination.count = count
              this.inlineTable.pagination.page = 1
            }

            // Extract user IDs from record values and load all users
            const fields = this.inlineTable.fields.filter(f => f.moduleField).map(f => f.moduleField)
            this.fetchUsers(fields, records)

            this.inlineTable.items = records.map(r => this.wrapRecord(r))
          })
      }).finally(() => {
        setTimeout(() => {
          this.processing = false
        }, 300)
      })
    },

    sorterStyle ({ key }, dir) {
      const { sort = '' } = this.inlineTable.filter

      const sortedFields = (sort.includes(',') ? sort.split(',') : [sort])

      const isSorted = sortedFields.map(v => v.trim()).some(value => {
        let valueDir = 'ASC'

        if (value.includes(' ')) {
          value = value.split(' ')[0]
          valueDir = 'DESC'
        }

        return valueDir === dir && value === key
      })

      return isSorted ? { color: 'black' } : {}
    },

    handleSort ({ key, sortable }) {
      if (!sortable) {
        return
      }

      if (this.inlineTable.sortBy !== key) {
        this.inlineTable.filter.sort = `${key}`
        this.inlineTable.sortDirecton = 'ASC'
      } else {
        if (this.inlineTable.sortDirecton === 'ASC') {
          this.inlineTable.filter.sort = `${key} DESC`
          this.inlineTable.sortDirecton = 'DESC'
        } else {
          this.inlineTable.filter.sort = `${key}`
          this.inlineTable.sortDirecton = 'ASC'
        }
      }
      this.inlineTable.sortBy = key
      this.refresh(true)
    },

    goToPage (page) {
      if (page >= 1) {
        this.inlineTable.filter.pageCursor = (this.inlineTable.pagination.pages[page - 1] || {}).cursor
        this.inlineTable.pagination.page = page
      } else {
        this.inlineTable.filter.pageCursor = this.inlineTable.filter[page]
        if (this.inlineTable.filter.pageCursor) {
          this.inlineTable.pagination.page += page === 'nextPage' ? 1 : -1
        } else {
          this.inlineTable.pagination.page = 1
        }
      }
      this.refresh()
    },

    wrapRecord (r, id) {
      if (r.id) {
        id = r.id
        r = r.r
      }

      return {
        r,
        id: id || r.recordID,
        _rowVariant: r.deletedAt ? 'danger' : undefined,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.default-cursor {
  cursor: default;
}
</style>
